import InputGroupContent from '@/components/elements/input'
import { Box, Flex, Heading } from '@chakra-ui/react'
import Submit from '@/components/elements/submit'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import Toast from '@/components/elements/toast'
import { useState, useEffect } from 'react'
import SpinnerLoading from '@/components/elements/spinner'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store/store'
import { couponsSchema } from '@/entities/schemas/coupons'
import { updateCouponById } from '@/services/api/coupons/coupons'
import { updateCouponFn } from '@/store/features/coupons'
import {
	couponRefrestUpdate,
	sessionRefrestUpdate,
} from '@/store/features/state'
import dateTimeConvert from '@/utils/datetime-convert'
import CouponsEntity, { TypesCoupon } from '@/entities/api/coupons/coupons'
import Select, { SelectOption } from '@/components/elements/select'
import { couponTypesOptions } from '@/utils/maps/coupon.types-map'
import { InferType } from 'yup'
import { useFormErrorToast } from '@/hooks/useFormErrorToast'

type UserFormValues = InferType<typeof couponsSchema>

const EditCoupon = ({
	coupon,
	close,
}: {
	coupon: CouponsEntity
	close: () => void
}) => {
	const token = useSelector((state: RootState) => state.auth.token)
	const {
		register,
		handleSubmit,
		control,
		formState: { errors },
	} = useForm<UserFormValues>({
		resolver: yupResolver(couponsSchema),
	})
	useFormErrorToast(errors)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [type, setType] = useState<SelectOption | null>(null)
	const dispatch = useDispatch<AppDispatch>()
	const [selectPrice, setSelectPrice] = useState<number | number>(0)

	useEffect(() => {
		if (coupon?.type) {
			const currentType = couponTypesOptions.find(
				(option) => option.value === coupon.type
			)
			if (currentType) {
				setType(currentType)
			}
		}
	}, [coupon])

	const onSubmit = async (dataYup: UserFormValues) => {
		try {
			setIsLoading(true)

			const data: Partial<CouponsEntity> = {
				expiration: dataYup.expiration ? dataYup.expiration : coupon.expiration,
				type: dataYup.type ? dataYup.type : coupon.type,
			}

			if (
				dataYup.type === TypesCoupon.AMOUNT ||
				(!dataYup.type && coupon.type === TypesCoupon.AMOUNT)
			) {
				if (!selectPrice && !coupon.amount) {
					Toast({ type: 'info', message: 'El monto es requerido' })
					setIsLoading(false)
					return
				}
				data.amount = selectPrice ? selectPrice : coupon.amount
			}

			if (
				dataYup.type === TypesCoupon.DISCOUNT ||
				(!dataYup.type && coupon.type === TypesCoupon.DISCOUNT)
			) {
				if (!dataYup.discount && !coupon.discount) {
					Toast({ type: 'info', message: 'El descuento es requerido' })
					setIsLoading(false)
					return
				}
				data.discount = dataYup.discount ? dataYup.discount : coupon.discount
			}

			const response = await updateCouponById(
				token ?? '',
				coupon?._id ?? '',
				data
			)

			if (response?.status === 500 || response?.status === 409) {
				Toast({
					type: 'error',
					message: 'Hubo un error al actualizar el cupón',
				})
				setIsLoading(false)
				return
			}

			if (response?.status === 401) {
				dispatch(sessionRefrestUpdate())
				setIsLoading(false)
				return
			}

			Toast({ type: 'success', message: 'Cupón actualizado correctamente' })
			dispatch(updateCouponFn({ ...coupon, ...data }))
			dispatch(couponRefrestUpdate())
			setIsLoading(false)
			close()
		} catch (error) {
			Toast({
				type: 'error',
				message: 'Algo salió mal al actualizar el cupón',
			})
			setIsLoading(false)
		}
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Flex w='100%' h='full' flexDirection='column' alignItems='center'>
				<Box
					w='full'
					display='flex'
					justifyContent='start'
					alignItems='center'
					mb='2'
				>
					<Heading
						as='h4'
						fontSize={{ base: 'lg', md: 'xl' }}
						color='black'
						fontWeight='900'
					>
						Información general
					</Heading>
				</Box>

				<Select
					name='type'
					label='Tipo del cupón'
					selectValue={setType}
					placeholder='Selecciona el tipo del cupón'
					options={couponTypesOptions}
					control={control}
					isRequired={true}
					defaultValue={coupon?.type}
				/>

				{(type?.value === TypesCoupon.AMOUNT ||
					(!type && coupon?.type === TypesCoupon.AMOUNT)) && (
					<InputGroupContent
						label='Monto en pesos (CO)'
						placeholder='Ingresa el monto'
						required
						isPrice={true}
						defaultValue={coupon?.amount ?? 0}
						change={(e: React.ChangeEvent<HTMLInputElement>) => {
							setSelectPrice(Number(e.target.value) || 0)
						}}
					/>
				)}

				{(type?.value === TypesCoupon.DISCOUNT ||
					(!type && coupon?.type === TypesCoupon.DISCOUNT)) && (
					<InputGroupContent
						label='Descuento en porcentaje (%)'
						placeholder='Ingresa el descuento'
						name='discount'
						type='text'
						defaultValue={coupon?.discount?.toString() ?? ''}
						register={register}
						errors={errors}
					/>
				)}

				<InputGroupContent
					label='Fecha de vencimiento del cupón'
					name='expiration'
					type='datetime-local'
					defaultValue={
						coupon?.expiration ? dateTimeConvert(coupon.expiration) : ''
					}
					register={register}
					errors={errors}
				/>

				<Box pt='4' pb='8'>
					{isLoading ? <SpinnerLoading /> : <Submit text='Actualizar cupón' />}
				</Box>
			</Flex>
		</form>
	)
}

export default EditCoupon
